import { debounce, SerializedData, Store } from 'bernie-core';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import { action, makeObservable, observable } from 'mobx';
import { UserInfo, UserSignIn, Program } from 'models';
import { AuthState, UserAuthSource } from '../sources';
import isEqual from 'lodash.isequal';

const STORAGE_LISTENING_INTERVAL = 25; //in ms

interface InternalAuthState {
  accessToken: string;
  idToken: string;
}

export class UserAuthStore extends Store {
  @observable
  public currentUser: UserInfo;

  @observable
  public authenticated: AuthState;

  @observable
  public authorized: AuthState;

  @observable
  public userProgramRelationships;

  private internalAuthState: InternalAuthState;

  private readonly userAuthSource: UserAuthSource;

  // private createProgramApiPath = getInternalApiPath('users', 'createprogramrelationship').fullPath;
  // private saveProgramApiPath = getInternalApiPath('users', 'saveprogramrelationship').fullPath;

  public constructor(userAuthSource: UserAuthSource, state: SerializedData = {}, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    // Needed for mobx 6 but doesn't exist in mobx 4/5
    makeObservable(this);

    this.currentUser = null;
    this.authenticated = AuthState.UNKNOWN;
    this.authorized = AuthState.UNKNOWN;
    this.userProgramRelationships = null;
    this.userAuthSource = userAuthSource;
    this.internalAuthState = null;

    // listening to local storage changes, if something changed, refresh auth state
    if (typeof window !== 'undefined') {
      this.storageListener = debounce(this.storageListener.bind(this), STORAGE_LISTENING_INTERVAL);
      window.addEventListener('storage', this.storageListener);
    }
  }

  private storageListener() {
    this.didInternalAuthStateChange().then((changed) => {
      if (changed) {
        this.checkAuth();
      }
    });
  }

  public isInLoginRedirect() {
    return this.userAuthSource.isInLoginRedirect();
  }

  // This is called from the Auth Callback page that Okta redirects to.
  // Its job is to turn that action into a logged-in session and does that
  // by asking the lower auth layers to figure this out.
  @action
  public createAuthSessionOrLogoutTo(unAuthedPath: string) {
    this.userAuthSource
      .createAuthenticatedSession()
      .then(() => {
        // A previous dev said:
        // Need to trigger check auth and can't rely on storage listener as
        // documentation suggests that the storage listener is not triggered for documents
        // that make the storage change.
        this.checkAuth();
      })
      .catch((err) => {
        // Ideally want to send the error to the path, so that it can display the error message
        // but we don't that at the moment
        this.logout(unAuthedPath);
      });
  }

  public async authRedirect(params) {
    return this.userAuthSource.authRedirect(params);
  }

  public initiateLogin(redirection: boolean, fromUri: string = null) {
    if (this.userAuthSource) {
      this.userAuthSource.initiateLogin(redirection, fromUri);
    }
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this.userAuthSource, data.userAuthSource);
    this.userAuthSource.hydrate();
    this.checkAuth();
  }

  public hydrateHistory = (history) => {
    this.userAuthSource.hydrateHistory(history);
  };

  private async fetchUserInfo(): Promise<UserInfo> {
    return this.userAuthSource.fetchUserInfo();
  }

  private async fetchUserProgramRelationships() {
    return this.userAuthSource.getProgramRelationships(this.currentUser);
  }

  private async didInternalAuthStateChange() {
    const currentInternalAuthState = await this.getCurrentInternalAuthState();

    return !isEqual(currentInternalAuthState, this.internalAuthState);
  }

  private async getCurrentInternalAuthState() {
    const accessToken = await this.userAuthSource.getAccessToken();
    const idToken = await this.userAuthSource.getIdToken();

    return { accessToken, idToken };
  }

  // Intentionally named checkAuth as it checks both authentication & authorization state
  @action
  private async checkAuth() {
    const authenticated: AuthState = await this.userAuthSource.isAuthenticated();

    if (authenticated === AuthState.TRUE) {
      this.fetchUserInfo()
        .then(
          action('authenticated', (userInfo) => {
            this.authenticated = authenticated;
            this.currentUser = userInfo;
            this.checkAuthorization();
          }),
        )
        .catch((error) => {
          this.logout('/home');
        });
    } else if (authenticated === AuthState.FALSE) {
      this.authorized = AuthState.FALSE;
      this.authenticated = AuthState.FALSE;
      this.currentUser = null;
      this.userProgramRelationships = null;
    } else {
      this.authorized = AuthState.UNKNOWN;
      this.authenticated = AuthState.UNKNOWN;
      this.currentUser = null;
      this.userProgramRelationships = null;
    }

    this.internalAuthState = await this.getCurrentInternalAuthState();
  }

  public updateUserProgramRelationship(
    brand: string,
    posa: string,
    network: string,
    partnerId: string,
    isExisting: boolean,
    programReference?: string,
    affiliatePartnershipStatus?: string,
    accountName?: string,
  ) {
    return fetch(`/api/v1/users/${this.currentUser.email}/programs`, {
      method: isExisting ? 'PUT' : 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: this.internalAuthState.accessToken,
        email: this.currentUser.email,
        brand,
        posa,
        network,
        partnerId,
        programReference,
        affiliatePartnershipStatus,
        accountName,
      }),
    }).then((response) =>
      response.json().then((data) => {
        data['status'] = response.status;

        // this will refresh the user program relationships
        this.checkAuthorization();

        return data;
      }),
    );
  }

  public signIn({ username, password, deviceToken }: UserSignIn) {
    return this.userAuthSource.signIn({ username, password, deviceToken });
  }

  public signInWithToken(token: string) {
    return this.userAuthSource.signInWithToken(token);
  }

  @action
  private checkAuthorization() {
    this.fetchUserProgramRelationships()
      .then(
        action('authorized', (programRelationships) => {
          this.userProgramRelationships = programRelationships;

          this.authorized =
            programRelationships.body.filter((programRelationship) => {
              return programRelationship.affiliatePartnership.partnershipStatus === 'APPROVED';
            }).length > 0
              ? AuthState.TRUE
              : AuthState.FALSE;
        }),
      )
      .catch((error) => {
        this.authorized = AuthState.UNKNOWN;
      });
  }

  @action
  public async logout(logoutPath: string) {
    await this.userAuthSource.revokeAndLogout(logoutPath);
    this.currentUser = null;
    this.authenticated = AuthState.FALSE;
    this.authorized = AuthState.FALSE;
    this.userProgramRelationships = null;
    this.internalAuthState = null;
  }

  @action
  public getRelationshipForProgram(program: Program) {
    let programRelationship = null;
    const userInfo = this.currentUser;
    const userId = userInfo && userInfo.email ? userInfo.email : null;

    if (userId) {
      const userProgram = this.userProgramRelationships
        ? this.userProgramRelationships.body.filter((userProg) => {
            return (
              userProg.id.userId === userId && userProg.id.brand === program.brand && userProg.id.posa === program.posa
            );
          })
        : [];

      if (userProgram.length) {
        programRelationship = userProgram[0];
      }
    }

    return programRelationship;
  }
}
